import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { getData, postData, LIVE_API } from "../../services/FetchNodeservices";

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const pipelineRef = useRef(null);
  const isReturningFromCandidates = useRef(false);

  // User details
  const user = useMemo(() => {
    return JSON.parse(localStorage.getItem("USER")) || {};
  }, []);

  const userId = user?.id || null;

  // States
  const [teamMembers, setTeamMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [joinings, setJoinings] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]);

  // Get today's local date in YYYY-MM-DD format
  const todayStr = useMemo(() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const todaysTasks = useMemo(() => {
    return tasks.filter(t => t.date === todayStr);
  }, [tasks, todayStr]);
  const [interviews, setInterviews] = useState([]);
  const [matrixData, setMatrixData] = useState([]);
  const [matrixSearch, setMatrixSearch] = useState("");
  const [selectedAssociate, setSelectedAssociate] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [loading, setLoading] = useState(true);

  // Helper date parser for various string formats (DD-MM-YYYY, YYYY-MM-DD, ISO, etc.)
  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    const str = String(dateStr).trim();

    const dmyMatch = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (dmyMatch) {
      const day = Number(dmyMatch[1]);
      const month = Number(dmyMatch[2]) - 1;
      const year = Number(dmyMatch[3]);
      const d = new Date(year, month, day);
      if (!isNaN(d.getTime())) {
        d.setHours(0, 0, 0, 0);
        return d;
      }
    }

    const ymdMatch = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (ymdMatch) {
      const year = Number(ymdMatch[1]);
      const month = Number(ymdMatch[2]) - 1;
      const day = Number(ymdMatch[3]);
      const d = new Date(year, month, day);
      if (!isNaN(d.getTime())) {
        d.setHours(0, 0, 0, 0);
        return d;
      }
    }

    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      d.setHours(0, 0, 0, 0);
      return d;
    }

    return null;
  };

  // Helper date normalizer for month filtering
  const getMonthYearString = (dateStr) => {
    if (!dateStr) return null;
    const date = parseDateString(dateStr);
    if (!date || isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(date);
  };

  // Helper to normalize associate name spaces
  const cleanName = (name) => {
    if (!name) return "";
    return name.replace(/\s+/g, " ").trim();
  };

  // Helper to normalize candidate status variations into standard pipeline stages
  const normalizeCandidateStatus = (rawStatus) => {
    if (!rawStatus) return "Screening";
    const s = String(rawStatus).trim();

    if (
      s === "Feedback Pending" ||
      s === "L1 Feedback Pending" ||
      s === "Profile Shared with Client"
    ) {
      return "Feedback Pending";
    }

    if (
      s === "Shortlisted" ||
      s === "Shortlist" ||
      s === "Profile Shortlisted" ||
      s.startsWith("Shortlist")
    ) {
      return "Shortlisted";
    }

    if (
      s === "Interview L1" ||
      s === "Interview line-up" ||
      s === "Interview Scheduled" ||
      s === "Interview Process" ||
      s === "Interview Completed" ||
      s.startsWith("Technical Round-1")
    ) {
      return "Interview L1";
    }

    if (
      s === "Interview L2" ||
      s.startsWith("Technical Round-2")
    ) {
      return "Interview L2";
    }

    if (s === "Face to Face") {
      return "Face to Face";
    }

    if (
      s === "Selected" ||
      s === "Candidate Selected"
    ) {
      return "Selected";
    }

    if (
      s === "Offered" ||
      s === "Offer Released" ||
      s === "Offer Accepted"
    ) {
      return "Offered";
    }

    if (
      s === "Joining Pending" ||
      s === "Pending for Joining"
    ) {
      return "Joining Pending";
    }

    if (
      s === "Joined" ||
      s === "Profile Onboard"
    ) {
      return "Joined";
    }

    if (
      s === "Rejected" ||
      s === "Reject" ||
      s === "Interview Reject" ||
      s === "Interview Rejected" ||
      s === "Screen Reject" ||
      s === "Screening rejected" ||
      s === "Resume Not Shortlisted" ||
      s === "Rejected by Candidate" ||
      s === "Rejected by Client"
    ) {
      return "Rejected";
    }

    if (
      s === "No Show" ||
      s === "Not Attended Interview"
    ) {
      return "No Show";
    }

    if (
      s === "Hold" ||
      s === "On Hold" ||
      s === "Profile On-Hold"
    ) {
      return "Hold";
    }

    if (
      s === "Backout" ||
      s === "Drop" ||
      s === "Dropped" ||
      s === "Dropped / Not Interested"
    ) {
      return "Backout";
    }

    if (s === "Duplicate") {
      return "Duplicate";
    }

    return "Screening";
  };

  // Candidate Details drill-down states
  const [viewMode, setViewMode] = useState("dashboard"); // "dashboard" or "candidates"
  const [candidatesList, setCandidatesList] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidateFilter, setCandidateFilter] = useState({
    recruiter: "",
    company: "",
    status: "",
  });
  const [candidateSearchText, setCandidateSearchText] = useState("");
  const [candidateStartDate, setCandidateStartDate] = useState("");
  const [candidateEndDate, setCandidateEndDate] = useState("");
  const [showColCustomizer, setShowColCustomizer] = useState(false);
  const [visibleCols, setVisibleCols] = useState({
    candidate_id: true,
    name: true,
    email: true,
    phone: true,
    qualification: true,
    experience: true,
    current_company: true,
    designation: true,
    current_ctc: true,
    expected_ctc: true,
    notice_period: true,
    hometown: true,
    status: true,
    date: true,
    resume: true
  });

  // Tabs for widgets
  const [onboardingTab, setOnboardingTab] = useState("new"); // "new" (future DOJ) vs "past" (past DOJ)
  const [interviewTab, setInterviewTab] = useState("new"); // "new" (upcoming date) vs "past" (past date)

  // Add Task Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [assigneeName, setAssigneeName] = useState("");
  const [profile, setProfile] = useState("");
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [targetCv, setTargetCv] = useState(0);
  const [cvSourced, setCvSourced] = useState(0);
  const [dueDate, setDueDate] = useState("");
  const [taskStatus, setTaskStatus] = useState("Pending");

  // Fetch all required data
  const fetchDashboardData = async () => {
    if (!userId) {
      toast.warning('User ID not found. Please log in again.');
      return;
    }
    setLoading(true);
    try {
      // 1. Fetch team members
      const teamRes = await getData(`team-structure/team/${userId}`);
      if (teamRes?.status) {
        setTeamMembers(teamRes.team || []);
        // Pre-select first team member or self if no team members
        if (teamRes.team?.length > 0) {
          setAssigneeName(teamRes.team[0].name);
        } else {
          setAssigneeName(`${user.first_name || ""} ${user.last_name || ""}`.trim());
        }
      }

      // 2. Fetch Working Tasks
      const tasksRes = await postData("workingtask/fetch", { user_id: userId });
      if (tasksRes?.status) {
        setTasks(tasksRes.data || []);
      }

      // 3. Fetch Placement Joinings
      const joinRes = await getData(`placementtracker/fetch/${userId}`);
      if (joinRes?.status) {
        setJoinings(joinRes.data || []);
      }

      // 4. Fetch Interview Pipeline
      const interviewRes = await postData("interview/fetch", { user_id: userId });
      if (interviewRes?.status) {
        setInterviews(interviewRes.data || []);
      }

      // 5. Fetch Position Matrix Data
      const matrixRes = await getData(`positionmatrix/fetchJobData/${userId}`);
      if (matrixRes?.status) {
        setMatrixData(matrixRes.data || []);
      }

      // 6. Fetch Candidates for logged in manager & hierarchy
      const candRes = await getData(`candidate-report/fetch/${userId}`);
      if (candRes?.status) {
        setAllCandidates(candRes.data || []);
      }
    } catch (error) {
      console.error("Error loading manager dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [userId]);

  // Handle Add Task Submission
  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!assigneeName || !profile || !dueDate) {
      toast.warning("Please fill in the required fields (Assignee, Profile, and Due Date).");
      return;
    }

    const todayStr = new Date().toISOString().split("T")[0];

    const payload = {
      user_id: userId,
      employeename: assigneeName,
      profile: profile,
      company: company || "N/A",
      location: location || "Remote",
      task_description: taskDescription,
      priority: priority,
      target_cv: Number(targetCv) || 0,
      cv_sourced: Number(cvSourced) || 0,
      due_date: dueDate,
      status: taskStatus,
      date: todayStr,
    };

    try {
      // In the backend, checkPermission is checked. We send user_id, it checks role info.
      const res = await postData("workingtask/add", payload);
      if (res?.status) {
        toast.success("Task assigned successfully!");
        setIsModalOpen(false);
        // Reset form fields
        setProfile("");
        setCompany("");
        setLocation("");
        setTaskDescription("");
        setPriority("Medium");
        setTargetCv(0);
        setCvSourced(0);
        setDueDate("");
        setTaskStatus("Pending");
        // Reload tasks list
        fetchDashboardData();
      } else {
        toast.error(res?.message || "Failed to add working task");
      }
    } catch (err) {
      console.error("Add task error:", err);
      toast.error("An error occurred while adding the task");
    }
  };

  // Helper date normalizer
  const parseDate = (dVal) => {
    if (!dVal) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dVal)) return new Date(dVal);
    const parsed = new Date(dVal);
    return isNaN(parsed) ? null : parsed;
  };

  // 1. Team Members derived formatting
  const formattedTeam = useMemo(() => {
    return teamMembers.map((m, idx) => {
      const colors = [
        "from-indigo-500 to-purple-500",
        "from-emerald-400 to-teal-600",
        "from-amber-400 to-orange-500",
        "from-pink-500 to-rose-600",
        "from-sky-400 to-blue-600",
      ];
      return {
        ...m,
        gradColor: colors[idx % colors.length],
      };
    });
  }, [teamMembers]);

  // Build recursive tree from flat formattedTeam array
  const teamTree = useMemo(() => {
    const nodeMap = {};
    formattedTeam.forEach((member) => {
      if (member.userId) {
        nodeMap[String(member.userId)] = {
          ...member,
          children: []
        };
      }
    });

    const roots = [];
    formattedTeam.forEach((member) => {
      const node = member.userId ? nodeMap[String(member.userId)] : null;
      if (!node) return;

      const parentId = member.reportingManagerId ? String(member.reportingManagerId) : null;

      if (!parentId || parentId === String(userId) || !nodeMap[parentId]) {
        roots.push(node);
      } else {
        nodeMap[parentId].children.push(node);
      }
    });

    return roots;
  }, [formattedTeam, userId]);

  // Complete tree with current manager as the root node
  const fullTree = useMemo(() => {
    return {
      userId: userId,
      name: `${user.first_name || ""} ${user.last_name || ""}`.trim() || "Manager",
      designation: user.designation_name || "Manager",
      office: "Main Office",
      gradColor: "from-indigo-600 to-purple-800",
      children: teamTree
    };
  }, [teamTree, userId, user]);

  // 2. Onboarding Candidates formatting & classification
  const displayJoinings = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sorted = [...joinings].sort((a, b) => new Date(a.doj) - new Date(b.doj));

    if (onboardingTab === "new") {
      // DOJ >= today
      return sorted.filter((j) => {
        const dObj = parseDate(j.doj);
        return dObj ? dObj >= today : true;
      });
    } else {
      // DOJ < today
      return sorted.filter((j) => {
        const dObj = parseDate(j.doj);
        return dObj ? dObj < today : false;
      });
    }
  }, [joinings, onboardingTab]);

  // 3. Onboarding Interview Pipeline formatting & classification
  const displayInterviews = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sorted = [...interviews].sort(
      (a, b) => new Date(a.interview_date) - new Date(b.interview_date)
    );

    if (interviewTab === "new") {
      // interview_date >= today
      return sorted.filter((i) => {
        const dObj = parseDate(i.interview_date);
        return dObj ? dObj >= today : true;
      });
    } else {
      // interview_date < today
      return sorted.filter((i) => {
        const dObj = parseDate(i.interview_date);
        return dObj ? dObj < today : false;
      });
    }
  }, [interviews, interviewTab]);

  // 4. Selected Candidates (Final Placements)
  const selectedCandidates = useMemo(() => {
    // Placement tracker tracks placed/finalized candidates
    return joinings;
  }, [joinings]);

  const totalPlacementAmount = useMemo(() => {
    return selectedCandidates.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  }, [selectedCandidates]);

  // Extract unique months from allCandidates and matrixData, plus populate all 12 months for current/previous years
  const uniqueMonths = useMemo(() => {
    const monthsSet = new Set();

    // Add all months from candidate data
    allCandidates.forEach((c) => {
      const monthStr = getMonthYearString(c.uploaded_on || c.application_date || c.updated_on);
      if (monthStr) monthsSet.add(monthStr);
    });

    // Add all months from position matrix data
    matrixData.forEach((row) => {
      const monthStr = getMonthYearString(row.position_received_on);
      if (monthStr) monthsSet.add(monthStr);
    });

    // Populate all 12 months of current year and previous year so manager can select any month
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    for (let yr of [currentYear, currentYear - 1]) {
      for (let m = 0; m < 12; m++) {
        const d = new Date(yr, m, 1);
        const mStr = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(d);
        monthsSet.add(mStr);
      }
    }

    return Array.from(monthsSet).sort((a, b) => new Date(b) - new Date(a));
  }, [allCandidates, matrixData]);

  // For the Performance Pipeline, we keep the full matrixData and filter the candidate counts dynamically by month
  const monthFilteredMatrixData = useMemo(() => {
    return matrixData;
  }, [matrixData]);

  // Helper to check if a candidate matches the selected month
  const matchesMonth = (dateStr) => {
    if (selectedMonth === "All") return true;
    const mStr = getMonthYearString(dateStr);
    return mStr === selectedMonth;
  };

  // Group candidate counts by req_id and filter by the selected month dynamically
  const candidateCountsByReqId = useMemo(() => {
    const counts = {};
    allCandidates.forEach((c) => {
      const dateVal = c.uploaded_on || c.application_date || c.updated_on;
      if (!matchesMonth(dateVal)) return;

      const reqId = c.req_id;
      if (!reqId) return;

      if (!counts[reqId]) {
        counts[reqId] = {
          profiles_shared: 0,
          feedback_pending: 0,
          screening: 0,
          shortlisted: 0,
          interview_l1: 0,
          interview_l2: 0,
          face_to_face: 0,
          selected: 0,
          offered: 0,
          joining_pending: 0,
          joined: 0,
          rejected: 0,
          no_show: 0,
          hold: 0,
          backout: 0,
          duplicate: 0,
        };
      }

      counts[reqId].profiles_shared += 1;

      const rawStatus = c.candidate_status || c.status;
      const status = normalizeCandidateStatus(rawStatus);

      if (status === "Feedback Pending") counts[reqId].feedback_pending += 1;
      else if (status === "Screening") counts[reqId].screening += 1;
      else if (status === "Shortlisted") counts[reqId].shortlisted += 1;
      else if (status === "Interview L1") counts[reqId].interview_l1 += 1;
      else if (status === "Interview L2") counts[reqId].interview_l2 += 1;
      else if (status === "Face to Face") counts[reqId].face_to_face += 1;
      else if (status === "Selected") counts[reqId].selected += 1;
      else if (status === "Offered") counts[reqId].offered += 1;
      else if (status === "Joining Pending") counts[reqId].joining_pending += 1;
      else if (status === "Joined") counts[reqId].joined += 1;
      else if (status === "Rejected") counts[reqId].rejected += 1;
      else if (status === "No Show") counts[reqId].no_show += 1;
      else if (status === "Hold") counts[reqId].hold += 1;
      else if (status === "Backout") counts[reqId].backout += 1;
      else if (status === "Duplicate") counts[reqId].duplicate += 1;
      else counts[reqId].screening += 1;
    });
    return counts;
  }, [allCandidates, selectedMonth]);

const getDynamicCounts = (row) => {
  // Backend already calculates these values
  if (selectedMonth === "All") {
    return {
      profiles_shared: Number(row.profiles_shared) || 0,
      feedback_pending: Number(row.feedback_pending) || 0,
      screening: Number(row.screening) || 0,
      shortlisted: Number(row.shortlisted) || 0,
      interview_l1: Number(row.interview_l1) || 0,
      interview_l2: Number(row.interview_l2) || 0,
      face_to_face: Number(row.face_to_face) || 0,
      selected: Number(row.selected) || 0,
      offered: Number(row.offered) || 0,
      joining_pending: Number(row.joining_pending) || 0,
      joined: Number(row.joined) || 0,
      rejected: Number(row.rejected) || 0,
      no_show: Number(row.no_show) || 0,
      hold: Number(row.hold) || 0,
      backout: Number(row.backout) || 0,
      duplicate: Number(row.duplicate) || 0,
    };
  }

  // For month filtering, use candidate data
  return candidateCountsByReqId[String(row.req_id)] || {
    profiles_shared: 0,
    feedback_pending: 0,
    screening: 0,
    shortlisted: 0,
    interview_l1: 0,
    interview_l2: 0,
    face_to_face: 0,
    selected: 0,
    offered: 0,
    joining_pending: 0,
    joined: 0,
    rejected: 0,
    no_show: 0,
    hold: 0,
    backout: 0,
    duplicate: 0,
  };
};

  // Filter / Aggregate Position Matrix Data dynamically based on selectedAssociate tab
  const filteredMatrixData = useMemo(() => {
    if (selectedAssociate === "All") {
      // Aggregate data per associate
      const map = {};

      // Initialize map with all team members to ensure they all show up!
      teamMembers.forEach((m) => {
        if (m.name) {
          const cName = cleanName(m.name);
          map[cName] = {
            associate_name: cName,
            profiles_shared: 0,
            feedback_pending: 0,
            screening: 0,
            shortlisted: 0,
            interview_l1: 0,
            interview_l2: 0,
            face_to_face: 0,
            selected: 0,
            offered: 0,
            joining_pending: 0,
            joined: 0,
            rejected: 0,
            no_show: 0,
            hold: 0,
            backout: 0,
            duplicate: 0,
          };
        }
      });

      monthFilteredMatrixData.forEach((row) => {
        const name = cleanName(row.associate_name) || "Unassigned";
        if (!map[name]) {
          map[name] = {
            associate_name: name,
            profiles_shared: 0,
            feedback_pending: 0,
            screening: 0,
            shortlisted: 0,
            interview_l1: 0,
            interview_l2: 0,
            face_to_face: 0,
            selected: 0,
            offered: 0,
            joining_pending: 0,
            joined: 0,
            rejected: 0,
            no_show: 0,
            hold: 0,
            backout: 0,
            duplicate: 0,
          };
        }
        const counts = getDynamicCounts(row);
        map[name].profiles_shared += counts.profiles_shared;
        map[name].feedback_pending += counts.feedback_pending;
        map[name].screening += counts.screening;
        map[name].shortlisted += counts.shortlisted;
        map[name].interview_l1 += counts.interview_l1;
        map[name].interview_l2 += counts.interview_l2;
        map[name].face_to_face += counts.face_to_face;
        map[name].selected += counts.selected;
        map[name].offered += counts.offered;
        map[name].joining_pending += counts.joining_pending;
        map[name].joined += counts.joined;
        map[name].rejected += counts.rejected;
        map[name].no_show += counts.no_show;
        map[name].hold += counts.hold;
        map[name].backout += counts.backout;
        map[name].duplicate += counts.duplicate;
      });

      let result = Object.values(map);
      if (matrixSearch) {
        const s = matrixSearch.toLowerCase();
        result = result.filter(row => row.associate_name?.toLowerCase().includes(s));
      }
      return result;
    } else {
      // Group requirements by Company Name for the selected associate (show total sum per company)
      const map = {};
      const targetAssociateNormalized = cleanName(selectedAssociate);
      const associateData = monthFilteredMatrixData.filter(row => cleanName(row.associate_name) === targetAssociateNormalized);

      associateData.forEach((row) => {
        const companyKey = row.company_name || "Unassigned";
        if (!map[companyKey]) {
          map[companyKey] = {
            company_name: companyKey,
            profiles_shared: 0,
            feedback_pending: 0,
            screening: 0,
            shortlisted: 0,
            interview_l1: 0,
            interview_l2: 0,
            face_to_face: 0,
            selected: 0,
            offered: 0,
            joining_pending: 0,
            joined: 0,
            rejected: 0,
            no_show: 0,
            hold: 0,
            backout: 0,
            duplicate: 0,
          };
        }
        const counts = getDynamicCounts(row);
        map[companyKey].profiles_shared += counts.profiles_shared;
        map[companyKey].feedback_pending += counts.feedback_pending;
        map[companyKey].screening += counts.screening;
        map[companyKey].shortlisted += counts.shortlisted;
        map[companyKey].interview_l1 += counts.interview_l1;
        map[companyKey].interview_l2 += counts.interview_l2;
        map[companyKey].face_to_face += counts.face_to_face;
        map[companyKey].selected += counts.selected;
        map[companyKey].offered += counts.offered;
        map[companyKey].joining_pending += counts.joining_pending;
        map[companyKey].joined += counts.joined;
        map[companyKey].rejected += counts.rejected;
        map[companyKey].no_show += counts.no_show;
        map[companyKey].hold += counts.hold;
        map[companyKey].backout += counts.backout;
        map[companyKey].duplicate += counts.duplicate;
      });

      let result = Object.values(map);
      if (matrixSearch) {
        const s = matrixSearch.toLowerCase();
        result = result.filter(row => row.company_name?.toLowerCase().includes(s));
      }
      return result;
    }
  }, [monthFilteredMatrixData, teamMembers, selectedAssociate, matrixSearch, candidateCountsByReqId]);

  // Unique Associate Names for Tab menu (derived from BOTH teamMembers AND monthFilteredMatrixData to show all members)
  const uniqueAssociates = useMemo(() => {
    const names = new Set();

    // Add all reports recursively mapped
    teamMembers.forEach((m) => {
      if (m.name) names.add(cleanName(m.name));
    });

    // Add any names that actually exist in monthFilteredMatrixData
    monthFilteredMatrixData.forEach((d) => {
      if (d.associate_name) names.add(cleanName(d.associate_name));
    });

    return ["All", ...Array.from(names)];
  }, [teamMembers, monthFilteredMatrixData]);

  // Reset search filter when switching tabs
  useEffect(() => {
    setMatrixSearch("");
  }, [selectedAssociate]);

  // Scroll to Performance Pipeline when returning to dashboard
  useEffect(() => {
    if (viewMode === "dashboard" && isReturningFromCandidates.current) {
      isReturningFromCandidates.current = false;
      setTimeout(() => {
        if (pipelineRef.current) {
          pipelineRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  }, [viewMode]);

  // Format currency
  const formatCurrency = (val) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  // Click handler on matrix cells
  const handleCountClick = async (clickedRow, clickedStatus) => {
    const recruiterVal = selectedAssociate === "All" ? cleanName(clickedRow.associate_name) : cleanName(selectedAssociate);
    const companyVal = selectedAssociate === "All" ? "" : clickedRow.company_name;

    setCandidateFilter({
      recruiter: recruiterVal,
      company: companyVal,
      status: clickedStatus,
    });

    setViewMode("candidates");
    setLoadingCandidates(true);

    try {
      const candData = allCandidates.length > 0 ? allCandidates : (await getData(`candidate-report/fetch/${userId}`))?.data || [];
      // Filter candidates list locally matching req_ids
      const matchingJobs = monthFilteredMatrixData.filter(job => {
        const matchesRecruiter = cleanName(job.associate_name) === cleanName(recruiterVal);
        const matchesCompany = !companyVal || job.company_name === companyVal;
        return matchesRecruiter && matchesCompany;
      });
      const matchingReqIds = matchingJobs.map(job => job.req_id);

      const filtered = candData.filter(c => {
        const matchesReq = matchingReqIds.includes(c.req_id);
        const normStatus = normalizeCandidateStatus(c.candidate_status || c.status);
        const matchesStatus = (clickedStatus === "Profiles Shared") || (normStatus === clickedStatus);
        const dateVal = c.uploaded_on || c.application_date || c.updated_on;
        const matchesDate = matchesMonth(dateVal);
        return matchesReq && matchesStatus && matchesDate;
      });
      setCandidatesList(filtered);
    } catch (err) {
      console.error("Error fetching candidates:", err);
      toast.error("An error occurred while loading candidates");
    } finally {
      setLoadingCandidates(false);
    }
  };

  // Helper helper to render clickable cell
  const renderClickableCell = (row, field, statusName, tdClass = "") => {
    const val = Number(row[field]) || 0;
    return (
      <td className={`px-3 py-3 text-center ${tdClass}`}>
        {val > 0 ? (
          <button
            onClick={() => handleCountClick(row, statusName)}
            className="hover:text-indigo-600 hover:underline transition cursor-pointer font-extrabold focus:outline-none"
          >
            {val}
          </button>
        ) : (
          <span className="text-gray-300 font-semibold">0</span>
        )}
      </td>
    );
  };

  // Filter candidates list based on search text and dates
  const filteredCandidatesForDisplay = useMemo(() => {
    let result = candidatesList;

    // Filter by dates
    if (candidateStartDate) {
      const start = new Date(candidateStartDate);
      start.setHours(0, 0, 0, 0);
      result = result.filter(c => {
        if (!c.uploaded_on) return false;
        const candDate = new Date(c.uploaded_on);
        return candDate >= start;
      });
    }

    if (candidateEndDate) {
      const end = new Date(candidateEndDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter(c => {
        if (!c.uploaded_on) return false;
        const candDate = new Date(c.uploaded_on);
        return candDate <= end;
      });
    }

    // Filter by search text
    if (candidateSearchText) {
      const s = candidateSearchText.toLowerCase();
      result = result.filter(c =>
        c.f_name?.toLowerCase().includes(s) ||
        c.l_name?.toLowerCase().includes(s) ||
        c.email_id?.toLowerCase().includes(s) ||
        c.phone_number?.includes(s) ||
        c.current_company?.toLowerCase().includes(s) ||
        c.designation?.toLowerCase().includes(s)
      );
    }

    return result;
  }, [candidatesList, candidateSearchText, candidateStartDate, candidateEndDate]);

  // Excel Export Handler
  const handleExportExcel = () => {
    if (filteredCandidatesForDisplay.length === 0) {
      toast.warning("No records to export.");
      return;
    }

    const exportRows = filteredCandidatesForDisplay.map((c) => {
      const row = {};
      if (visibleCols.candidate_id) row["Candidate ID"] = c.candidate_id;
      if (visibleCols.name) row["Name"] = `${c.f_name || ""} ${c.l_name || ""}`.trim();
      if (visibleCols.email) row["Email"] = c.email_id || "";
      if (visibleCols.phone) row["Phone"] = c.phone_number || "";
      if (visibleCols.qualification) row["Qualification"] = c.qualification || "";
      if (visibleCols.experience) row["Experience"] = c.experience || "";
      if (visibleCols.current_company) row["Current Company"] = c.current_company || "";
      if (visibleCols.designation) row["Designation"] = c.designation || "";
      if (visibleCols.current_ctc) row["Current CTC"] = c.current_ctc || "";
      if (visibleCols.expected_ctc) row["Expected CTC"] = c.expected_ctc || "";
      if (visibleCols.notice_period) row["Notice Period"] = c.notice_period || "";
      if (visibleCols.hometown) row["Hometown"] = c.hometown || "";
      if (visibleCols.status) row["Status"] = c.status || "";
      if (visibleCols.date) row["Date"] = c.uploaded_on ? new Date(c.uploaded_on).toLocaleDateString("en-IN") : "N/A";
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Candidates");
    XLSX.writeFile(workbook, `Candidates_${candidateFilter.recruiter || "all"}_${candidateFilter.status || "all"}.xlsx`);
    toast.success("Excel sheet exported successfully!");
  };

  // Performance Pipeline Excel Export Handler
  const exportPerformancePipelineToExcel = () => {
    if (filteredMatrixData.length === 0) {
      toast.warning("No records to export.");
      return;
    }

    const isAll = selectedAssociate === "All";
    const exportRows = filteredMatrixData.map((row) => {
      return {
        [isAll ? "Associate Name" : "Company Name"]: isAll ? (row.associate_name || "Unassigned") : (row.company_name || "N/A"),
        "Profiles Shared": row.profiles_shared || 0,
        "Feedback Pending": row.feedback_pending || 0,
        "Screening": row.screening || 0,
        "Shortlisted": row.shortlisted || 0,
        "Interview L1": row.interview_l1 || 0,
        "Interview L2": row.interview_l2 || 0,
        "Face to Face": row.face_to_face || 0,
        "Selected": row.selected || 0,
        "Offered": row.offered || 0,
        "Joining Pending": row.joining_pending || 0,
        "Joined": row.joined || 0,
        "Rejected": row.rejected || 0,
        "No Show": row.no_show || 0,
        "Hold": row.hold || 0,
        "Backout": row.backout || 0,
        "Duplicate": row.duplicate || 0,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Performance Pipeline");
    
    const formattedMonth = selectedMonth.replace(/\s+/g, "_");
    const formattedAssociate = selectedAssociate.replace(/\s+/g, "_");
    XLSX.writeFile(workbook, `Performance_Pipeline_${formattedAssociate}_${formattedMonth}.xlsx`);
    toast.success("Performance Pipeline exported successfully!");
  };

  // Stats Card calculations
  const todayPositions = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return matrixData.filter(row => {
      if (!row.position_received_on) return false;
      const posDate = parseDateString(row.position_received_on);
      return posDate && posDate.getTime() === today.getTime();
    }).length;
  }, [matrixData]);

  const activePositionsCount = useMemo(() => {
    return matrixData.filter(row => row.final_status === "Active").length;
  }, [matrixData]);

  const closedPositionsCount = useMemo(() => {
    return matrixData.filter(row => row.final_status === "Closed").length;
  }, [matrixData]);

  const revenueThisMonth = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-indexed

    return joinings.reduce((sum, item) => {
      if (!item.doj) return sum;
      const dojDate = parseDateString(item.doj);
      if (!dojDate || isNaN(dojDate.getTime())) return sum;

      const isCurrentMonth = dojDate.getFullYear() === currentYear && dojDate.getMonth() === currentMonth;
      if (isCurrentMonth) {
        return sum + (Number(item.amount) || 0);
      }
      return sum;
    }, 0);
  }, [joinings]);

  const todayCandidatesCount = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return allCandidates.filter(c => {
      const candDate = parseDateString(c.uploaded_on || c.application_date || c.updated_on);
      return candDate && candDate.getTime() === today.getTime();
    }).length;
  }, [allCandidates]);

  if (viewMode === "candidates") {
    return (
      <div className="space-y-6 pb-10 animate-fadeIn">
        {/* Header container */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 p-6 rounded-[32px] shadow-sm">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                isReturningFromCandidates.current = true;
                setViewMode("dashboard");
              }}
              className="px-4 py-2 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-2xl hover:text-gray-800 dark:hover:text-white transition active:scale-95 cursor-pointer flex items-center justify-center font-bold text-xs border border-gray-100 dark:border-gray-700"
            >
              &larr; Back to Dashboard
            </button>
            <div>
              <h2 className="text-xl font-black text-gray-800 dark:text-white tracking-tight leading-none">Candidate Pipeline Details</h2>
              <p className="text-xs text-gray-400 dark:text-gray-500 font-bold mt-2.5 flex flex-wrap gap-x-2 gap-y-1 items-center">
                <span>Recruiter: <strong className="text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded">{candidateFilter.recruiter}</strong></span>
                {candidateFilter.company && <span>&bull; Company: <strong className="text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded">{candidateFilter.company}</strong></span>}
                &bull; Status: <strong className="text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded">{candidateFilter.status}</strong>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs font-black text-emerald-600 dark:text-emerald-400 bg-emerald-55 dark:bg-emerald-950/40 px-3 py-1.5 rounded-2xl">
              {filteredCandidatesForDisplay.length} Records Found
            </span>
          </div>
        </div>

        {/* Controls Card */}
        <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 p-6 rounded-[32px] shadow-sm flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
          {/* Search bar and Date Filters */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
            {/* Search bar */}
            <div className="relative flex-1 max-w-md w-full">
              <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search by Name, Email, Phone, Company, or Designation..."
                value={candidateSearchText}
                onChange={(e) => setCandidateSearchText(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-[#f4f6fa]/85 dark:bg-gray-700 border border-gray-100 dark:border-gray-655 rounded-2xl text-xs focus:outline-none focus:ring-4 focus:ring-indigo-100/50 focus:border-indigo-500 transition duration-200 placeholder-gray-400 dark:placeholder-gray-500 text-gray-700 dark:text-gray-200 font-medium"
              />
            </div>

            {/* Date Filters */}
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">From:</span>
                <input
                  type="date"
                  value={candidateStartDate}
                  onChange={(e) => setCandidateStartDate(e.target.value)}
                  className="bg-[#f4f6fa]/85 dark:bg-gray-700 border border-gray-100 dark:border-gray-655 rounded-2xl text-xs px-3 py-2 text-gray-700 dark:text-gray-200 font-bold focus:outline-none focus:ring-4 focus:ring-indigo-100/50 focus:border-indigo-500 transition cursor-pointer"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">To:</span>
                <input
                  type="date"
                  value={candidateEndDate}
                  onChange={(e) => setCandidateEndDate(e.target.value)}
                  className="bg-[#f4f6fa]/85 dark:bg-gray-700 border border-gray-100 dark:border-gray-655 rounded-2xl text-xs px-3 py-2 text-gray-700 dark:text-gray-200 font-bold focus:outline-none focus:ring-4 focus:ring-indigo-100/50 focus:border-indigo-500 transition cursor-pointer"
                />
              </div>
              {(candidateStartDate || candidateEndDate) && (
                <button
                  onClick={() => {
                    setCandidateStartDate("");
                    setCandidateEndDate("");
                  }}
                  title="Clear Date Filters"
                  className="px-2.5 py-2 bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/40 text-rose-600 dark:text-rose-450 rounded-2xl text-xs font-black transition duration-150 cursor-pointer"
                >
                  🧹 Clear
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 relative shrink-0 justify-end">
            {/* Column Customizer button */}
            <button
              onClick={() => setShowColCustomizer(!showColCustomizer)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-605 border border-gray-100 dark:border-gray-655 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white rounded-2xl text-xs font-black transition duration-150 flex items-center gap-2 cursor-pointer"
            >
              ⚙️ Customize Columns
            </button>

            {/* Customizer Dropdown Popup */}
            {showColCustomizer && (
              <div className="absolute right-0 top-12 z-30 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-3xl p-4 shadow-xl w-60 space-y-3 animate-fadeIn">
                <div className="flex items-center justify-between border-b border-gray-50 dark:border-gray-700 pb-2">
                  <span className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Select Visible Columns</span>
                  <button
                    onClick={() => setShowColCustomizer(false)}
                    className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-xs font-black"
                  >
                    &times;
                  </button>
                </div>
                <div className="max-h-60 overflow-y-auto space-y-2 pr-1 no-scrollbar">
                  {Object.keys(visibleCols).map((colKey) => (
                    <label key={colKey} className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-300 font-semibold cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={visibleCols[colKey]}
                        onChange={() => setVisibleCols(prev => ({ ...prev, [colKey]: !prev[colKey] }))}
                        className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-indigo-600 focus:ring-indigo-500/20 h-4 w-4 cursor-pointer"
                      />
                      <span>{colKey.replace("_", " ").toUpperCase()}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Excel Export button */}
            <button
              onClick={handleExportExcel}
              className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-xs font-black shadow-lg shadow-indigo-100 hover:shadow-indigo-200 transition duration-150 flex items-center gap-2 cursor-pointer"
            >
              📥 Export Excel
            </button>
          </div>
        </div>

        {/* Candidates Table Card */}
        <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-[32px] p-6 shadow-sm">
          {loadingCandidates ? (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm font-bold text-gray-400 dark:text-gray-500">Loading Candidate Pipeline Details...</p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-50 dark:border-gray-700 rounded-2xl no-scrollbar">
              <table className="w-full text-left text-xs font-bold text-gray-500 dark:text-gray-400 min-w-[1200px]">
                <thead>
                  <tr className="bg-[#fdfdff] dark:bg-gray-800/80 border-b border-gray-50 dark:border-gray-700 text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-widest font-black">
                    {visibleCols.candidate_id && <th className="px-4 py-3.5 whitespace-nowrap">Candidate ID</th>}
                    {visibleCols.date && <th className="px-4 py-3.5 whitespace-nowrap">Date</th>}
                    {visibleCols.name && <th className="px-4 py-3.5">Name</th>}
                    {visibleCols.email && <th className="px-4 py-3.5">Email</th>}
                    {visibleCols.phone && <th className="px-4 py-3.5">Phone</th>}
                    {visibleCols.qualification && <th className="px-4 py-3.5">Qualification</th>}
                    {visibleCols.experience && <th className="px-4 py-3.5">Experience</th>}
                    {visibleCols.current_company && <th className="px-4 py-3.5">Current Company</th>}
                    {visibleCols.designation && <th className="px-4 py-3.5">Designation</th>}
                    {visibleCols.current_ctc && <th className="px-4 py-3.5">Current CTC</th>}
                    {visibleCols.expected_ctc && <th className="px-4 py-3.5">Expected CTC</th>}
                    {visibleCols.notice_period && <th className="px-4 py-3.5">Notice</th>}
                    {visibleCols.hometown && <th className="px-4 py-3.5">Hometown</th>}
                    {visibleCols.status && <th className="px-4 py-3.5">Status</th>}
                    {visibleCols.resume && <th className="px-4 py-3.5">Resume</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50/60 dark:divide-gray-700">
                  {filteredCandidatesForDisplay.length === 0 ? (
                    <tr>
                      <td colSpan="14" className="text-center py-12 text-gray-400 dark:text-gray-500 italic font-medium">
                        No candidates found matching the selected status or filters.
                      </td>
                    </tr>
                  ) : (
                    filteredCandidatesForDisplay.map((candidate, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/40 dark:hover:bg-gray-700/20 transition">
                        {visibleCols.candidate_id && (
                          <td className="px-4 py-3 text-gray-400 dark:text-gray-500 whitespace-nowrap">
                            #{candidate.candidate_id}
                          </td>
                        )}
                        {visibleCols.date && (
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-300 font-medium whitespace-nowrap">
                            {candidate.uploaded_on ? new Date(candidate.uploaded_on).toLocaleDateString("en-IN") : "N/A"}
                          </td>
                        )}
                        {visibleCols.name && (
                          <td className="px-4 py-3 text-gray-800 dark:text-gray-100 font-black whitespace-nowrap">
                            {`${candidate.f_name || ""} ${candidate.l_name || ""}`}
                          </td>
                        )}
                        {visibleCols.email && (
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-300 font-medium whitespace-nowrap">
                            {candidate.email_id || "N/A"}
                          </td>
                        )}
                        {visibleCols.phone && (
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-300 font-medium whitespace-nowrap">
                            {candidate.phone_number || "N/A"}
                          </td>
                        )}
                        {visibleCols.qualification && (
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {candidate.qualification || "N/A"}
                          </td>
                        )}
                        {visibleCols.experience && (
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {candidate.experience || "0"} Years
                          </td>
                        )}
                        {visibleCols.current_company && (
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {candidate.current_company || "N/A"}
                          </td>
                        )}
                        {visibleCols.designation && (
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {candidate.designation || "N/A"}
                          </td>
                        )}
                        {visibleCols.current_ctc && (
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {candidate.current_ctc || "N/A"}
                          </td>
                        )}
                        {visibleCols.expected_ctc && (
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {candidate.expected_ctc || "N/A"}
                          </td>
                        )}
                        {visibleCols.notice_period && (
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {candidate.notice_period || "0"} Days
                          </td>
                        )}
                        {visibleCols.hometown && (
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {candidate.hometown || "N/A"}
                          </td>
                        )}
                        {visibleCols.status && (
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${candidate.status === "Selected" || candidate.status === "Joined" || candidate.status === "Offered"
                                ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400"
                                : candidate.status === "Rejected" || candidate.status === "No Show" || candidate.status === "Backout"
                                  ? "bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400"
                                  : "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400"
                              }`}>
                              {candidate.status}
                            </span>
                          </td>
                        )}
                        {visibleCols.resume && (
                          <td className="px-4 py-3 whitespace-nowrap">
                            {candidate.resume && candidate.resume !== "no_resume.pdf" ? (
                              <a
                                href={`${LIVE_API}${candidate.resume_path}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 dark:bg-indigo-950/40 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 rounded-xl text-[10px] font-black uppercase tracking-wider transition duration-150"
                              >
                                📄 View Resume
                              </a>
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600 italic text-[11px]">No Resume</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Recursive Tree Node Renderer
  const renderTreeNode = (node, depth = 0) => {
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div key={node.userId || node.id} className="relative">
        {/* Card for member */}
        <div className={`relative flex items-center gap-3 border p-3 rounded-2xl transition-all duration-200 mt-2 mb-2 max-w-sm ${depth === 0
            ? "bg-indigo-50/70 dark:bg-indigo-950/20 border-indigo-200/80 dark:border-indigo-800/40 shadow-sm"
            : "bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 hover:shadow-md hover:-translate-y-0.5"
          }`}>
          {/* Left Avatar */}
          <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${node.gradColor || "from-indigo-500 to-purple-500"} text-white flex items-center justify-center font-black text-xs shadow-sm`}>
            {node.name?.[0] || "U"}
          </div>

          {/* Name & Designation Info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h4 className="text-xs font-black text-gray-800 dark:text-white leading-tight truncate">{node.name}</h4>
              {depth === 0 && (
                <span className="bg-indigo-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded uppercase leading-none">YOU</span>
              )}
            </div>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider mt-0.5 truncate">
              {node.designation || "Recruiter"}
            </p>
          </div>

          {/* Office badge */}
          <span className="text-[8px] bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-650 text-gray-500 dark:text-gray-400 font-bold px-1.5 py-0.5 rounded-md whitespace-nowrap">
            {node.office || "Remote"}
          </span>
        </div>

        {/* Children nodes list */}
        {hasChildren && (
          <div className="relative pl-12 mt-1 space-y-1">
            {/* Vertical connector line aligned with parent's avatar center at 30px */}
            <div className="absolute left-[30px] top-0 bottom-[27px] w-0.5 bg-gray-200 dark:bg-gray-700" />

            {node.children.map((child) => (
              <div key={child.userId || child.id} className="relative">
                {/* Horizontal connector line branching off parent's vertical line to child card boundary */}
                <div className="absolute left-[-18px] top-[27px] w-[18px] h-0.5 bg-gray-200 dark:bg-gray-700" />
                {renderTreeNode(child, depth + 1)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8 pb-10">
      {/* GREETING PANEL */}


      {loading && (
        <div className="flex flex-col items-center justify-center py-16 space-y-4">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm font-bold text-gray-400 dark:text-gray-500">Loading Manager Dashboard Hub...</p>
        </div>
      )}

      {!loading && (
        <div className="space-y-8 animate-fadeIn">
          {/* STATS CARDS ROW */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">

            {/* Card 1: Today's Positions */}
            <div
              onClick={() => navigate("/dashboard/myreport?tab=matrix&date=today")}
              className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-[24px] p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition duration-200 cursor-pointer flex flex-col justify-between h-36 group"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-500 text-white flex items-center justify-center shadow-md">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 .9-.75 1.6-1.6 1.6H5.4c-.9 0-1.6-.7-1.6-1.6v-4.25m16.5 0a2.44 2.44 0 00-2.25-2.25H5.4A2.44 2.44 0 003.15 14.15m17.1 0h-3.15m-10.8 0H3.15m3-3.75V5.6c0-.9.75-1.6 1.6-1.6h6.5c.9 0 1.6.75 1.6 1.6v4.8" />
                  </svg>
                </div>
                <span className="text-[9px] font-black tracking-widest text-gray-455 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 group-hover:bg-indigo-600 group-hover:text-white px-2.5 py-1 rounded-full transition duration-150 uppercase">
                  View
                </span>
              </div>
              <div className="mt-4">
                <h4 className="text-2xl font-black text-gray-800 dark:text-white leading-tight">
                  {todayPositions}
                </h4>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider mt-1">
                  Today's Positions
                </p>
              </div>
            </div>

            {/* Card 2: Active Positions */}
            <div
              onClick={() => navigate("/dashboard/myreport?tab=matrix&status=Active")}
              className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-[24px] p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition duration-200 cursor-pointer flex flex-col justify-between h-36 group"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-400 to-purple-500 text-white flex items-center justify-center shadow-md">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25A2.25 2.25 0 0113.5 8.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                  </svg>
                </div>
                <span className="text-[9px] font-black tracking-widest text-gray-455 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 group-hover:bg-indigo-600 group-hover:text-white px-2.5 py-1 rounded-full transition duration-150 uppercase">
                  View
                </span>
              </div>
              <div className="mt-4">
                <h4 className="text-2xl font-black text-gray-800 dark:text-white leading-tight">
                  {activePositionsCount.toLocaleString()}
                </h4>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider mt-1">
                  Active Positions
                </p>
              </div>
            </div>

            {/* Card 3: Closed Positions */}
            <div
              onClick={() => navigate("/dashboard/myreport?tab=matrix&status=Closed")}
              className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-[24px] p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition duration-200 cursor-pointer flex flex-col justify-between h-36 group"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 text-white flex items-center justify-center shadow-md">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0110 21a3.745 3.745 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.745 3.745 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                  </svg>
                </div>
                <span className="text-[9px] font-black tracking-widest text-gray-455 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 group-hover:bg-indigo-600 group-hover:text-white px-2.5 py-1 rounded-full transition duration-150 uppercase">
                  View
                </span>
              </div>
              <div className="mt-4">
                <h4 className="text-2xl font-black text-gray-800 dark:text-white leading-tight">
                  {closedPositionsCount.toLocaleString()}
                </h4>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider mt-1">
                  Closed Positions
                </p>
              </div>
            </div>

            {/* Card 4: Revenue This Month */}
            <div
              onClick={() => navigate("/dashboard/myreport?tab=placementtracker")}
              className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-[24px] p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition duration-200 cursor-pointer flex flex-col justify-between h-36 group"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shadow-md">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 8.25H9m6 3H9m3 6l-3-3h1.5a3 3 0 100-6M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-[9px] font-black tracking-widest text-gray-455 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 group-hover:bg-indigo-600 group-hover:text-white px-2.5 py-1 rounded-full transition duration-150 uppercase">
                  View
                </span>
              </div>
              <div className="mt-4">
                <h4 className="text-2xl font-black text-gray-800 dark:text-white leading-tight">
                  {formatCurrency(revenueThisMonth)}
                </h4>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider mt-1">
                  Revenue This Month
                </p>
              </div>
            </div>

            {/* Card 5: Today's Candidates */}
            <div
              onClick={() => navigate("/dashboard/myreport?tab=candidate&date=today")}
              className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-[24px] p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition duration-200 cursor-pointer flex flex-col justify-between h-36 group"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-400 to-rose-500 text-white flex items-center justify-center shadow-md">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.97 5.97 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.97 5.97 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                  </svg>
                </div>
                <span className="text-[9px] font-black tracking-widest text-gray-455 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 group-hover:bg-indigo-600 group-hover:text-white px-2.5 py-1 rounded-full transition duration-150 uppercase">
                  View
                </span>
              </div>
              <div className="mt-4">
                <h4 className="text-2xl font-black text-gray-800 dark:text-white leading-tight">
                  {todayCandidatesCount}
                </h4>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider mt-1">
                  Today's Candidates
                </p>
              </div>
            </div>

          </div>

          <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 items-start">

            {/* ================================================= */}
            {/* LEFT COLUMN: MAIN STATS & ACTIONS (60% WIDTH) */}
            {/* ================================================= */}
            <div className="lg:col-span-6 space-y-8">

              {/* ROW 1: Team Members Grid */}
              <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-3xl p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-black text-gray-800 dark:text-white tracking-tight leading-none">Recruiter Team</h3>
                    <p className="text-xs text-gray-400 dark:text-gray-500 font-bold mt-1">Directly reporting recruiters</p>
                  </div>
                  <span className="text-xs bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 font-black px-3 py-1.5 rounded-2xl border border-transparent dark:border-indigo-800/20">
                    {teamMembers.length} Members
                  </span>
                </div>

                {teamMembers.length === 0 ? (
                  <div className="bg-[#f8f9fc] dark:bg-gray-800/50 rounded-2xl py-8 text-center border border-dashed border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-400 dark:text-gray-500 font-bold">No direct reports currently mapped.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto no-scrollbar py-2">
                    <div className="min-w-[320px]">
                      {renderTreeNode(fullTree, 0, true)}
                    </div>
                  </div>
                )}
              </div>

              {/* ROW 2: Working Tasks Section */}
              <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-3xl p-6 shadow-sm space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-black text-gray-800 dark:text-white tracking-tight leading-none font-sans">Assign & Track Tasks</h3>
                    <p className="text-xs text-gray-400 dark:text-gray-500 font-bold mt-1">Assign deliverables to your recruiters and track status</p>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-auto flex-wrap">
                    <button
                      onClick={() => navigate("/dashboard/workingeod")}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-gray-750/30 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-150 dark:border-gray-600 text-gray-700 dark:text-gray-250 rounded-2xl text-xs font-black shadow-sm transition duration-200 active:scale-95 whitespace-nowrap"
                    >
                      📋 View All Tasks
                    </button>

                    <button
                      onClick={() => setIsModalOpen(true)}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-xs font-black shadow-lg shadow-indigo-100 dark:shadow-none transition duration-200 active:scale-95 whitespace-nowrap"
                    >
                      <span className="text-base leading-none font-light">+</span> Add & Assign Task
                    </button>
                  </div>
                </div>

                {/* Tasks Table */}
                <div className="overflow-x-auto border border-gray-100 dark:border-gray-700 rounded-2xl no-scrollbar">
                  <table className="w-full text-left text-xs font-bold text-gray-500 dark:text-gray-400">
                    <thead>
                      <tr className="bg-[#fdfdff] dark:bg-gray-800/80 border-b border-gray-50 dark:border-gray-700 text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-widest font-black">
                        <th className="px-4 py-3.5">Job / Profile</th>
                        <th className="px-4 py-3.5">Assignee</th>
                        <th className="px-4 py-3.5">Due Date</th>
                        <th className="px-4 py-3.5 text-center">Priority</th>
                        <th className="px-4 py-3.5 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50/60 dark:divide-gray-700">
                      {todaysTasks.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-8 text-gray-400 dark:text-gray-500 italic">
                            No tasks have been assigned for today.
                          </td>
                        </tr>
                      ) : (
                        todaysTasks.map((task, idx) => (
                          <tr key={idx} className="hover:bg-gray-50/40 dark:hover:bg-gray-700/20 transition">
                            <td className="px-4 py-3 text-gray-800 dark:text-gray-200">
                              <p className="font-black leading-tight">{task.profile}</p>
                              <p className="text-[10px] text-gray-400 dark:text-gray-500 font-semibold">{task.company || "N/A"}</p>
                            </td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300 font-semibold">
                              {task.employeename || "Unassigned"}
                            </td>
                            <td className="px-4 py-3 text-gray-400 dark:text-gray-500">
                              {task.due_date ? task.due_date : "-"}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-0.5 rounded-md text-[9px] font-black ${task.priority === "High"
                                  ? "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30"
                                  : task.priority === "Medium"
                                    ? "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30"
                                    : "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30"
                                }`}>
                                {task.priority || "Medium"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-0.5 rounded-md text-[9px] font-black ${task.status === "Completed"
                                  ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30"
                                  : task.status === "In Progress"
                                    ? "text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/30"
                                    : "text-amber-500 dark:text-amber-400 bg-amber-50/60 dark:bg-amber-950/20"
                                }`}>
                                {task.status || "Pending"}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>

            {/* ================================================= */}
            {/* RIGHT COLUMN: ONBOARDING & PIPELINE (40% WIDTH) */}
            {/* ================================================= */}
            <div className="lg:col-span-4 space-y-8">

              {/* WIDGET 1: Onboarding Candidates */}
              <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-3xl p-6 shadow-sm space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-black text-gray-800 dark:text-white tracking-tight leading-none">Onboarding Tracker</h3>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 font-bold mt-1">Pre-onboarding status by Date of Joining</p>
                  </div>
                  <button
                    onClick={() => navigate("/dashboard/myreport?tab=placementtracker")}
                    className="text-xs font-black text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition duration-150 cursor-pointer"
                  >
                    View All &rarr;
                  </button>
                </div>

                {/* Subtabs */}
                <div className="flex bg-[#f4f6fa] dark:bg-gray-700/50 p-1 rounded-2xl">
                  <button
                    onClick={() => setOnboardingTab("new")}
                    className={`flex-1 text-center py-2 rounded-xl text-xs font-black transition-all ${onboardingTab === "new"
                        ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm"
                        : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                      }`}
                  >
                    New Joining
                  </button>
                  <button
                    onClick={() => setOnboardingTab("past")}
                    className={`flex-1 text-center py-2 rounded-xl text-xs font-black transition-all ${onboardingTab === "past"
                        ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm"
                        : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                      }`}
                  >
                    Past Joining
                  </button>
                </div>

                {/* List */}
                <div className="space-y-3.5 max-h-[380px] overflow-y-auto pr-1 no-scrollbar">
                  {displayJoinings.length === 0 ? (
                    <div className="text-center py-10 text-gray-400 dark:text-gray-500 text-xs font-bold italic">
                      No joinings under this category
                    </div>
                  ) : (
                    displayJoinings.map((join, idx) => (
                      <div
                        key={idx}
                        className="bg-[#f8f9fc] dark:bg-gray-700/30 rounded-2xl p-4 border border-gray-100/60 dark:border-gray-700/60 flex items-center justify-between gap-3 hover:-translate-y-0.5 transition-all duration-200"
                      >
                        <div className="min-w-0 space-y-1">
                          <h4 className="text-xs font-black text-gray-800 dark:text-gray-100 truncate leading-none">{join.candidate_name}</h4>
                          <p className="text-[10px] text-gray-400 dark:text-gray-500 font-bold truncate leading-none">{join.position}</p>
                          <p className="text-[10px] text-indigo-600 dark:text-indigo-400 font-extrabold truncate leading-none">{join.company}</p>
                        </div>

                        <div className="text-right">
                          <span className="text-[10px] text-indigo-600 dark:text-indigo-400 font-black bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100/40 dark:border-indigo-900/40 px-2 py-1 rounded-lg whitespace-nowrap">
                            {join.doj ? new Date(join.doj).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) : "N/A"}
                          </span>
                          <p className="text-[9px] text-gray-400 dark:text-gray-500 font-semibold mt-1.5 uppercase leading-none">{join.status_joined || "Joined"}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* WIDGET 2: Interview Pipeline (new and past) */}
              <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-3xl p-6 shadow-sm space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-black text-gray-800 dark:text-white tracking-tight leading-none">Interview Pipeline</h3>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 font-bold mt-1">Live active rounds & schedules</p>
                  </div>
                  <button
                    onClick={() => navigate("/dashboard/myreport?tab=interview")}
                    className="text-xs font-black text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition duration-150 cursor-pointer"
                  >
                    View All &rarr;
                  </button>
                </div>

                {/* Subtabs */}
                <div className="flex bg-[#f4f6fa] dark:bg-gray-700/50 p-1 rounded-2xl">
                  <button
                    onClick={() => setInterviewTab("new")}
                    className={`flex-1 text-center py-2 rounded-xl text-xs font-black transition-all ${interviewTab === "new"
                        ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm"
                        : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                      }`}
                  >
                    Upcoming
                  </button>
                  <button
                    onClick={() => setInterviewTab("past")}
                    className={`flex-1 text-center py-2 rounded-xl text-xs font-black transition-all ${interviewTab === "past"
                        ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm"
                        : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                      }`}
                  >
                    Past Rounds
                  </button>
                </div>

                {/* List */}
                <div className="space-y-3.5 max-h-[380px] overflow-y-auto pr-1 no-scrollbar">
                  {displayInterviews.length === 0 ? (
                    <div className="text-center py-10 text-gray-400 dark:text-gray-500 text-xs font-bold italic">
                      No interviews mapped.
                    </div>
                  ) : (
                    displayInterviews.map((item, idx) => (
                      <div
                        key={idx}
                        className="bg-[#f8f9fc] dark:bg-gray-700/30 rounded-2xl p-4 border border-gray-100/60 dark:border-gray-700/60 flex items-center justify-between gap-3 hover:-translate-y-0.5 transition-all duration-200"
                      >
                        <div className="min-w-0 space-y-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-xs font-black text-gray-800 dark:text-gray-100 truncate leading-none">{item.candidate_name}</h4>
                            <span className="text-[8px] font-black uppercase text-indigo-500 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-1 py-0.2 rounded-md">
                              {item.stage || "L1"}
                            </span>
                          </div>
                          <p className="text-[10px] text-gray-400 dark:text-gray-500 font-bold truncate leading-none">{item.position}</p>
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 font-extrabold truncate leading-none">{item.company}</p>
                        </div>

                        <div className="text-right flex flex-col items-end gap-1">
                          <span className="text-[10px] text-gray-700 dark:text-gray-300 font-black bg-white dark:bg-gray-700 border border-gray-100 dark:border-gray-600 px-2 py-1 rounded-lg whitespace-nowrap leading-none">
                            {item.interview_date ? new Date(item.interview_date).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) : "N/A"}
                          </span>
                          {item.interview_time && (
                            <span className="text-[9px] text-indigo-500 dark:text-indigo-400 font-bold uppercase tracking-wider leading-none">
                              {item.interview_time}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* ROW 3: Selected Candidate & Amount (Right Bottom) */}
              <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-3xl p-6 shadow-sm space-y-6 animate-fadeIn">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center justify-between w-full sm:w-auto gap-4">
                    <div>
                      <h3 className="text-base font-black text-gray-800 dark:text-white tracking-tight leading-none">Placed Candidates</h3>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 font-bold mt-1">Selections & placements</p>
                    </div>
                    <button
                      onClick={() => navigate("/dashboard/myreport?tab=placementtracker")}
                      className="text-xs font-black text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition duration-150 cursor-pointer"
                    >
                      View All &rarr;
                    </button>
                  </div>

                  {/* Visual Revenue summary card */}
                  <div className="bg-indigo-50/60 dark:bg-indigo-950/20 border border-indigo-100/50 dark:border-indigo-900/30 rounded-2xl px-3 py-1.5 text-right">
                    <p className="text-[8px] text-indigo-400 dark:text-indigo-500 uppercase font-black tracking-widest leading-none">Total Revenue</p>
                    <p className="text-sm font-black text-indigo-700 dark:text-indigo-400 mt-0.5 leading-none">{formatCurrency(totalPlacementAmount)}</p>
                  </div>
                </div>

                {/* Candidate Placements list */}
                <div className="overflow-x-auto border border-gray-50 dark:border-gray-700 rounded-2xl no-scrollbar">
                  <table className="w-full text-left text-xs font-bold text-gray-500 dark:text-gray-400">
                    <thead>
                      <tr className="bg-[#fdfdff] dark:bg-gray-800/80 border-b border-gray-50 dark:border-gray-700 text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-widest font-black">
                        <th className="px-4 py-3.5">Candidate / Company</th>
                        <th className="px-4 py-3.5 text-right">Fee</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50/60 dark:divide-gray-700">
                      {selectedCandidates.length === 0 ? (
                        <tr>
                          <td colSpan="2" className="text-center py-6 text-gray-400 dark:text-gray-500 italic">
                            No placements found.
                          </td>
                        </tr>
                      ) : (
                        selectedCandidates.slice(0, 8).map((candidate, idx) => (
                          <tr key={idx} className="hover:bg-gray-50/40 dark:hover:bg-gray-700/20 transition">
                            <td className="px-4 py-2.5 text-gray-800 dark:text-gray-250 font-black">
                              <p className="leading-tight">{candidate.candidate_name}</p>
                              <p className="text-[9px] text-indigo-600 dark:text-indigo-400 font-bold mt-0.5">{candidate.company}</p>
                            </td>
                            <td className="px-4 py-2.5 text-right text-indigo-600 dark:text-indigo-400 font-black whitespace-nowrap">
                              {formatCurrency(candidate.amount)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ================================================= */}
      {/* POSITION MATRIX WIDGET (100% WIDTH) */}
      {/* ================================================= */}
      {!loading && (
        <div ref={pipelineRef} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-3xl p-6 shadow-sm space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-black text-gray-800 dark:text-white tracking-tight leading-none font-sans">Performance Pipeline</h3>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 font-bold">Track associate closure metrics and stages</p>
            </div>

            <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 w-full lg:w-auto justify-start sm:justify-end">
              {/* Month filter select */}
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Month:</span>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="bg-[#f4f6fa]/85 dark:bg-gray-700 border border-gray-100 dark:border-gray-650 rounded-2xl text-xs px-3 py-2 text-gray-700 dark:text-gray-200 font-bold focus:outline-none focus:ring-4 focus:ring-indigo-100/50 focus:border-indigo-500 transition cursor-pointer"
                >
                  <option value="All">📅 All Months</option>
                  {uniqueMonths.map((m, idx) => (
                    <option key={idx} value={m}>📅 {m}</option>
                  ))}
                </select>
              </div>

              {/* Search filter */}
              <div className="relative w-full sm:w-64 max-w-xs shrink-0">
                <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder={selectedAssociate === "All" ? "Search by Associate Name..." : "Search by Company/Position..."}
                  value={matrixSearch}
                  onChange={(e) => setMatrixSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-[#f4f6fa]/85 dark:bg-gray-700 border border-gray-100 dark:border-gray-650 rounded-2xl text-xs focus:outline-none focus:ring-4 focus:ring-indigo-100/50 focus:border-indigo-500 transition duration-200 placeholder-gray-400 dark:placeholder-gray-500 text-gray-700 dark:text-gray-200 font-medium"
                />
              </div>

              {/* Export Excel button */}
              <button
                onClick={exportPerformancePipelineToExcel}
                disabled={filteredMatrixData.length === 0}
                className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black rounded-2xl shadow-sm hover:shadow-md transition duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export Excel
              </button>
            </div>
          </div>

          {/* Associate Name horizontal tab filter */}
          {uniqueAssociates.length > 1 && (
            <div className="space-y-2">
              <p className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest leading-none">Filter by Associate</p>
              <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
                {uniqueAssociates.map((name, index) => {
                  const active = selectedAssociate === name;
                  return (
                    <button
                      key={index}
                      onClick={() => setSelectedAssociate(name)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-black transition whitespace-nowrap border ${active
                          ? "bg-indigo-600 border-indigo-600 text-white shadow-md dark:shadow-none"
                          : "bg-[#f8f9fc] dark:bg-gray-700 border border-gray-100 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200"
                        }`}
                    >
                      {name === "All" ? "⭐ All Associates" : `👤 ${name}`}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Matrix table with horizontal scroll */}
          <div className="overflow-x-auto border border-gray-50 dark:border-gray-700 rounded-2xl">
            <table className="w-full text-left text-xs font-bold text-gray-500 dark:text-gray-455 min-w-[1500px]">
              <thead>
                <tr className="bg-[#fdfdff] dark:bg-gray-800/80 border-b border-gray-50 dark:border-gray-700 text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-widest font-black">
                  <th className="px-4 py-3.5 whitespace-nowrap">
                    {selectedAssociate === "All" ? "Associate Name" : "Company Name"}
                  </th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap">Profiles Shared</th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap">Feedback Pending</th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap">Screening</th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap">Shortlisted</th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap">Interview L1</th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap">Interview L2</th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap">Face to Face</th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap text-emerald-600 bg-emerald-50/20 dark:bg-emerald-950/20">Selected</th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap text-emerald-700 bg-emerald-50/40 dark:bg-emerald-950/40">Offered</th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap">Joining Pending</th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap text-indigo-700 bg-indigo-50/40 dark:bg-indigo-950/40">Joined</th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap">Rejected</th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap">No Show</th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap">Hold</th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap">Backout</th>
                  <th className="px-3 py-3.5 text-center whitespace-nowrap">Duplicate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50/60 dark:divide-gray-700">
                {filteredMatrixData.length === 0 ? (
                  <tr>
                    <td colSpan="17" className="text-center py-8 text-gray-400 dark:text-gray-500 italic">
                      No matrix records found.
                    </td>
                  </tr>
                ) : (
                  filteredMatrixData.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/40 dark:hover:bg-gray-700/20 transition">
                      <td className="px-4 py-3 text-gray-800 dark:text-gray-100 font-black whitespace-nowrap">
                        {selectedAssociate === "All" ? (
                          `👤 ${row.associate_name || "Unassigned"}`
                        ) : (
                          `🏢 ${row.company_name || "N/A"}`
                        )}
                      </td>
                      {renderClickableCell(row, "profiles_shared", "Profiles Shared", "text-gray-600 dark:text-gray-300 font-semibold")}
                      {renderClickableCell(row, "feedback_pending", "Feedback Pending", "text-amber-500 font-semibold")}
                      {renderClickableCell(row, "screening", "Screening", "text-blue-500 font-semibold")}
                      {renderClickableCell(row, "shortlisted", "Shortlisted", "text-[#6366f1] font-semibold")}
                      {renderClickableCell(row, "interview_l1", "Interview L1", "text-purple-500 font-semibold")}
                      {renderClickableCell(row, "interview_l2", "Interview L2", "text-purple-600 font-semibold")}
                      {renderClickableCell(row, "face_to_face", "Face to Face", "text-purple-700 font-semibold")}
                      {renderClickableCell(row, "selected", "Selected", "text-emerald-600 dark:text-emerald-400 font-bold bg-emerald-50/20 dark:bg-emerald-950/20")}
                      {renderClickableCell(row, "offered", "Offered", "text-emerald-700 dark:text-emerald-300 font-black bg-emerald-50/40 dark:bg-emerald-950/40")}
                      {renderClickableCell(row, "joining_pending", "Joining Pending", "text-indigo-500 font-semibold")}
                      {renderClickableCell(row, "joined", "Joined", "text-indigo-700 dark:text-indigo-300 font-black bg-indigo-50/40 dark:bg-indigo-950/40")}
                      {renderClickableCell(row, "rejected", "Rejected", "text-red-500 font-semibold")}
                      {renderClickableCell(row, "no_show", "No Show", "text-red-600 font-semibold")}
                      {renderClickableCell(row, "hold", "Hold", "text-gray-400 dark:text-gray-500 font-semibold")}
                      {renderClickableCell(row, "backout", "Backout", "text-orange-500 font-semibold")}
                      {renderClickableCell(row, "duplicate", "Duplicate", "text-rose-400 font-semibold")}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ================================================= */}
      {/* ADD & ASSIGN TASK MODAL POPUP */}
      {/* ================================================= */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 dark:bg-black/60 backdrop-blur-sm animate-fadeIn">
          {/* Modal box */}
          <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-[32px] w-full max-w-xl shadow-2xl p-6 md:p-8 space-y-6 max-h-[90vh] overflow-y-auto no-scrollbar">

            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 pb-4">
              <div>
                <h3 className="text-lg font-black text-gray-800 dark:text-white tracking-tight leading-none">Assign Deliverable Task</h3>
                <p className="text-xs text-gray-400 dark:text-gray-500 font-bold mt-1">Fill the details to assign a recruiting assignment.</p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition duration-150 text-xl font-bold"
              >
                &times;
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleAddTask} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                {/* Recruiter Assignee Select */}
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest block">
                    Assign To Recruiter <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 dark:bg-gray-700/50 text-gray-850 dark:text-gray-100 border border-gray-100 dark:border-gray-600 focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition duration-200 text-xs font-semibold"
                    value={assigneeName}
                    onChange={(e) => setAssigneeName(e.target.value)}
                    required
                  >
                    <option value="" disabled>Select Team Member</option>
                    <option value={`${user.first_name || ""} ${user.last_name || ""}`.trim()}>
                      {`${user.first_name || ""} ${user.last_name || ""}`.trim()} (Self)
                    </option>
                    {teamMembers.map((m, idx) => (
                      <option key={idx} value={m.name}>
                        {m.name} ({m.designation || "Recruiter"})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Job / Profile */}
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest block">
                    Job / Profile Profile <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. React Native Dev"
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 dark:bg-gray-700/50 text-gray-850 dark:text-gray-100 border border-gray-100 dark:border-gray-600 focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition duration-200 text-xs font-semibold"
                    value={profile}
                    onChange={(e) => setProfile(e.target.value)}
                    required
                  />
                </div>

              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                {/* Company Name */}
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest block">
                    Company Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Morgan Stanley"
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 dark:bg-gray-700/50 text-gray-850 dark:text-gray-100 border border-gray-100 dark:border-gray-600 focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition duration-200 text-xs font-semibold"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </div>

                {/* Location */}
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest block">
                    Location
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Mumbai, India"
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 dark:bg-gray-700/50 text-gray-850 dark:text-gray-100 border border-gray-100 dark:border-gray-600 focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition duration-200 text-xs font-semibold"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  />
                </div>

              </div>

              {/* Task Description */}
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest block">
                  Task Description
                </label>
                <textarea
                  rows="3"
                  placeholder="Summarize CV filters, salary caps, and timeline requirements..."
                  className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 dark:bg-gray-700/50 text-gray-850 dark:text-gray-100 border border-gray-100 dark:border-gray-600 focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition duration-200 text-xs font-semibold resize-none"
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

                {/* Priority */}
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest block">
                    Priority
                  </label>
                  <select
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 dark:bg-gray-700/50 text-gray-850 dark:text-gray-100 border border-gray-100 dark:border-gray-600 focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition duration-200 text-xs font-semibold"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>

                {/* Target CVs */}
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest block">
                    Target CVs
                  </label>
                  <input
                    type="number"
                    min="0"
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 dark:bg-gray-700/50 text-gray-850 dark:text-gray-100 border border-gray-100 dark:border-gray-600 focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition duration-200 text-xs font-semibold"
                    value={targetCv}
                    onChange={(e) => setTargetCv(Number(e.target.value))}
                  />
                </div>

                {/* CVs Sourced */}
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest block">
                    CVs Sourced
                  </label>
                  <input
                    type="number"
                    min="0"
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 dark:bg-gray-700/50 text-gray-850 dark:text-gray-100 border border-gray-100 dark:border-gray-600 focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition duration-200 text-xs font-semibold"
                    value={cvSourced}
                    onChange={(e) => setCvSourced(Number(e.target.value))}
                  />
                </div>

              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                {/* Due Date */}
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest block">
                    Due Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 dark:bg-gray-700/50 text-gray-850 dark:text-gray-100 border border-gray-100 dark:border-gray-600 focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition duration-200 text-xs font-semibold"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    required
                  />
                </div>

                {/* Initial Status */}
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest block">
                    Status
                  </label>
                  <select
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 dark:bg-gray-700/50 text-gray-850 dark:text-gray-100 border border-gray-100 dark:border-gray-600 focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition duration-200 text-xs font-semibold"
                    value={taskStatus}
                    onChange={(e) => setTaskStatus(e.target.value)}
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>

              </div>

              {/* Buttons */}
              <div className="flex gap-4 pt-4 border-t border-gray-100 dark:border-gray-700 mt-6">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 py-3 text-center text-xs font-black rounded-2xl bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 text-center text-xs font-black rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-100 dark:shadow-none transition"
                >
                  Assign Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Inline animations styling */}
      <style>
        {`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.15s ease-out forwards;
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        `}
      </style>
    </div>
  );
}
